// §11.10 / §9.1 — Clock & Calendar's registration stub.
//
// **It was an eager constant until M64.3, and is now a lazy stub** (D-566). The reason is a measurement
// rather than a preference: §34.11 makes the month grid read-write, and the calendar view, the `.ics`
// reader/writer and the event store together put **21 104 bytes** into the entry chunk — carried by
// every visitor, including one who never opens a clock. That is more than five of the wave's
// registration stubs put together.
//
// D-511 already made this decision for Workbench's *new* apps and D-507's standing note anticipated
// exactly this case ("if that total starts to matter the answer is to make Workbench lazy as a group").
// An app that grows a second, larger surface is the same situation from the other direction, so it gets
// the same answer. Nothing about the clock needs to be eager: §11.10's tray flyout draws its own month
// from `shared/calendar.ts`, and the **reminders keep working with this window shut** because they are
// the kernel's (`kernel/calendar/`), not the view's — which is the whole point of §34.11's design and
// is what makes laziness safe here rather than a regression.
import type { ClockShellAccess, InProcessApp } from '@hangar/shared';
import { createLazyApp } from '@/gamekit/lazy';
import { COPY } from '@/copy';

export function createClockApp(access: ClockShellAccess): InProcessApp {
  return createLazyApp({
    app: {
      id: 'clock',
      name: 'Clock & Calendar',
      icon: { glyph: 'clock', hue: 28 },
      keywords: ['time', 'timer', 'alarm', 'calendar', 'date', 'month', 'event', 'ics', 'reminder'],
      window: { width: 880, height: 640, minWidth: 420, minHeight: 380, multiWindow: false },
      // §34.11 — **New event** is in the jump list as well as in the month bar, so a calendar entry can
      // be made from the taskbar without looking at the month first.
      jumpList: [{ id: 'new-event', label: COPY.calendar.newEvent }],
    },
    // The `import()` is written here on purpose: a thunk built anywhere else moves the split point.
    load: () => import('./Clock'),
    props: { access },
  });
}
